import React, { useEffect, useMemo, useState } from 'react';
import {
  Wallet, TrendingUp, TrendingDown, Plus, Trash2, X, Filter, CalendarRange, Tag, User as UserIcon,
  Pencil, Check, Download, Loader2, Send,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { Patient } from '../types';
import {
  CashEntry,
  CashEntryType,
  DEFAULT_EXPENSE_CATEGORIES,
  DEFAULT_INCOME_CATEGORIES,
  availableMonths,
  currentMonthKey,
  dailySeries,
  filterEntries,
  formatCurrency,
  formatMonthLabel,
  loadCustomCategories,
  loadEntries,
  MIGRATED_KEY,
  parseAmount,
  saveCustomCategories,
  STORAGE_KEY,
  summarize,
  todayIso,
  totalsByCategory,
} from '../utils/cashBook';
import {
  createCashEntry,
  deleteCashEntry,
  fetchCashEntries,
  importCashEntries,
  requestCashReport,
  updateCashEntryAmount,
  type CashReportRequest,
} from '../services/cashBookApi';
import { describeError } from '../utils/describeError';
import { isProfessionalRole } from '../utils/patientAccess';

interface FinanceProps {
  patients?: Patient[];
  /** Serviços cadastrados na Agenda: viram categorias de entrada. */
  serviceNames?: string[];
  /** Bloco #66: profissionais (e administrador) para o filtro. */
  professionals?: { id: string; name: string }[];
  /** Perfil logado: o profissional só vê os próprios lançamentos. */
  activeProfile?: { id?: string; name?: string; role?: string } | null;
}

type SubTab = 'geral' | 'entradas' | 'saidas';

const CHART_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#0ea5e9', '#ec4899'];

const Finance: React.FC<FinanceProps> = ({ patients = [], serviceNames = [], professionals = [], activeProfile = null }) => {
  const [subTab, setSubTab] = useState<SubTab>('geral');
  // Bloco #66: o profissional enxerga só o que é dele (a API também limita);
  // o filtro de profissional fica travado nele.
  const lockedProfessionalId = isProfessionalRole(activeProfile?.role) && activeProfile?.id ? String(activeProfile.id) : '';
  const [professionalFilter, setProfessionalFilter] = useState<string>(lockedProfessionalId);
  const [serviceFilter, setServiceFilter] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [editingAmount, setEditingAmount] = useState<{ id: string; value: string; error?: string; saving?: boolean } | null>(null);
  const [entries, setEntries] = useState<CashEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [month, setMonth] = useState(() => currentMonthKey());

  // Filtros das sub-abas
  const [patientFilter, setPatientFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [modalType, setModalType] = useState<CashEntryType | null>(null);

  const [customIncome, setCustomIncome] = useState<string[]>(() => loadCustomCategories('entrada'));
  const [customExpense, setCustomExpense] = useState<string[]>(() => loadCustomCategories('saida'));

  // Lançamentos vêm do banco. Na primeira abertura, o que estava salvo neste
  // navegador sobe para o banco (a API ignora o que outro navegador já subiu).
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const locais = loadEntries();
        if (locais.length && !localStorage.getItem(MIGRATED_KEY)) {
          await importCashEntries(locais);
          localStorage.setItem(MIGRATED_KEY, new Date().toISOString());
          localStorage.removeItem(STORAGE_KEY);
        }
        const data = await fetchCashEntries();
        if (ativo) setEntries(Array.isArray(data) ? data : []);
      } catch (err) {
        if (ativo) setLoadError(describeError(err));
      } finally {
        if (ativo) setLoading(false);
      }
    })();
    return () => { ativo = false; };
  }, []);
  useEffect(() => { saveCustomCategories('entrada', customIncome); }, [customIncome]);
  useEffect(() => { saveCustomCategories('saida', customExpense); }, [customExpense]);

  /** Serviços cadastrados + categorias padrão + as criadas pelo usuário. */
  const incomeCategories = useMemo(
    () => [...new Set([...serviceNames, ...DEFAULT_INCOME_CATEGORIES, ...customIncome])].filter(Boolean),
    [serviceNames, customIncome],
  );
  const expenseCategories = useMemo(
    () => [...new Set([...DEFAULT_EXPENSE_CATEGORIES, ...customExpense])].filter(Boolean),
    [customExpense],
  );

  const months = useMemo(() => availableMonths(entries), [entries]);

  const periodFilters = useMemo(() => (from || to ? { from, to } : { month }), [from, to, month]);

  // Bloco #66: profissional, tipo de serviço e paciente valem para a lista,
  // o total e os cartões (Entradas/Saídas/Saldo) de todas as abas.
  const scopeFilters = useMemo(
    () => ({
      ...periodFilters,
      professionalId: (lockedProfessionalId || professionalFilter) || undefined,
      serviceName: serviceFilter || undefined,
      patientId: patientFilter || undefined,
    }),
    [periodFilters, lockedProfessionalId, professionalFilter, serviceFilter, patientFilter],
  );
  const monthEntries = useMemo(() => filterEntries(entries, scopeFilters), [entries, scopeFilters]);
  const summary = useMemo(() => summarize(monthEntries), [monthEntries]);

  const incomeRows = useMemo(
    () => filterEntries(entries, { ...scopeFilters, type: 'entrada' }),
    [entries, scopeFilters],
  );
  const expenseRows = useMemo(
    () => filterEntries(entries, { ...scopeFilters, type: 'saida', category: categoryFilter || undefined }),
    [entries, scopeFilters, categoryFilter],
  );

  /** Serviços para o filtro: os cadastrados + os que já aparecem nos lançamentos. */
  const serviceOptions = useMemo(() => {
    const set = new Set<string>(serviceNames);
    entries.forEach((e) => { if (e.serviceName) set.add(e.serviceName); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [serviceNames, entries]);

  const professionalOptions = useMemo(() => {
    const map = new Map<string, string>();
    professionals.forEach((p) => map.set(String(p.id), p.name));
    entries.forEach((e) => { if (e.professionalId && !map.has(e.professionalId)) map.set(e.professionalId, e.professionalName || e.professionalId); });
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [professionals, entries]);

  const series = useMemo(() => dailySeries(monthEntries, month), [monthEntries, month]);
  const expenseByCategory = useMemo(
    () => totalsByCategory(monthEntries.filter((e) => e.type === 'saida')),
    [monthEntries],
  );
  const incomeByCategory = useMemo(
    () => totalsByCategory(monthEntries.filter((e) => e.type === 'entrada')),
    [monthEntries],
  );

  const handleCreate = async (entry: CashEntry) => {
    let saved: CashEntry;
    try {
      saved = await createCashEntry({
        type: entry.type,
        date: entry.date,
        amount: entry.amount,
        category: entry.category,
        description: entry.description,
        patientId: entry.patientId,
      });
    } catch (err) {
      window.alert(`Não foi possível salvar o lançamento: ${describeError(err)}`);
      return;
    }
    setEntries((prev) => [saved, ...prev]);
    if (entry.type === 'entrada' && !incomeCategories.includes(entry.category)) {
      setCustomIncome((prev) => [...prev, entry.category]);
    }
    if (entry.type === 'saida' && !expenseCategories.includes(entry.category)) {
      setCustomExpense((prev) => [...prev, entry.category]);
    }
    setModalType(null);
  };

  /** Bloco #65: lápis → edita só o valor; cancelar mantém o original. */
  const startEditAmount = (row: CashEntry) => {
    setEditingAmount({ id: row.id, value: row.amount.toFixed(2).replace('.', ',') });
  };
  const saveEditAmount = async () => {
    if (!editingAmount) return;
    const amount = parseAmount(editingAmount.value);
    if (!editingAmount.value.trim() || !(amount > 0)) {
      setEditingAmount({ ...editingAmount, error: 'Informe um valor em R$ maior que zero (ex.: 150,00).' });
      return;
    }
    setEditingAmount({ ...editingAmount, saving: true, error: undefined });
    try {
      const saved = await updateCashEntryAmount(editingAmount.id, amount);
      setEntries((prev) => prev.map((e) => (e.id === saved.id ? { ...e, ...saved } : e)));
      setEditingAmount(null);
    } catch (err) {
      setEditingAmount({ ...editingAmount, saving: false, error: describeError(err) });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir este lançamento do livro caixa?')) return;
    try {
      await deleteCashEntry(id);
    } catch (err) {
      window.alert(`Não foi possível excluir: ${describeError(err)}`);
      return;
    }
    setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const clearPeriod = () => { setFrom(''); setTo(''); };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-16">
      {loadError && (
        <div role="alert" className="px-5 py-4 rounded-2xl text-xs font-bold bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 border border-rose-100 dark:border-rose-900/50">
          Não foi possível carregar o Livro Caixa: {loadError}
        </div>
      )}
      {loading && (
        <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">Carregando lançamentos...</p>
      )}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black dark:text-white tracking-tight flex items-center gap-3">
            <Wallet className="text-indigo-500" size={28} /> Livro Caixa
          </h1>
          <p className="text-[11px] font-black uppercase tracking-widest text-slate-500 mt-1">
            Entradas e saídas do consultório
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <select
            value={month}
            onChange={(e) => { setMonth(e.target.value); clearPeriod(); }}
            className="px-5 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-[11px] font-black uppercase tracking-widest text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 shadow-sm cursor-pointer"
          >
            {months.map((m) => <option key={m} value={m}>{formatMonthLabel(m)}</option>)}
          </select>
          <button
            onClick={() => setModalType('entrada')}
            className="px-6 py-3 bg-emerald-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg hover:bg-emerald-700 active:scale-95 transition-all"
          >
            <Plus size={14} /> Nova Entrada
          </button>
          <button
            onClick={() => setModalType('saida')}
            className="px-6 py-3 bg-rose-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg hover:bg-rose-700 active:scale-95 transition-all"
          >
            <Plus size={14} /> Nova Saída
          </button>
          {/* Bloco #66 */}
          <button
            onClick={() => setShowReport(true)}
            className="px-6 py-3 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg hover:bg-indigo-700 transition-all"
          >
            <Download size={14} /> Exportar relatório
          </button>
        </div>
      </div>

      {/* Sub-abas: Geral | Entradas | Saídas */}
      <div className="flex bg-slate-100 dark:bg-slate-900 p-1.5 rounded-2xl w-full sm:w-fit">
        {([
          { id: 'geral', label: 'Geral' },
          { id: 'entradas', label: 'Entradas' },
          { id: 'saidas', label: 'Saídas' },
        ] as const).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={`flex-1 sm:flex-none px-2 sm:px-8 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
              subTab === tab.id
                ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filtro de período compartilhado */}
      <div className="flex flex-wrap items-end gap-4 p-5 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex items-center gap-2 text-slate-400">
          <CalendarRange size={16} />
          <span className="text-[10px] font-black uppercase tracking-widest">Período</span>
        </div>
        <div className="space-y-1 flex-1 sm:flex-none min-w-[130px]">
          <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">De</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="block w-full sm:w-auto px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div className="space-y-1 flex-1 sm:flex-none min-w-[130px]">
          <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Até</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="block w-full sm:w-auto px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        {/* Bloco #66: filtros combináveis com o período (e com o paciente, na aba Entradas) */}
        {!lockedProfessionalId && (
          <FilterSelect
            icon={<UserIcon size={14} />}
            label="Profissional"
            value={professionalFilter}
            onChange={setProfessionalFilter}
            options={[{ value: '', label: 'Visão geral (todos)' }, ...professionalOptions.map((p) => ({ value: p.id, label: p.name }))]}
          />
        )}
        <FilterSelect
          icon={<Tag size={14} />}
          label="Tipo de serviço"
          value={serviceFilter}
          onChange={setServiceFilter}
          options={[{ value: '', label: 'Visão geral (todos)' }, ...serviceOptions.map((s) => ({ value: s, label: s }))]}
        />
        {(from || to) && (
          <button onClick={clearPeriod} className="px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-rose-500 transition-colors">
            Limpar período
          </button>
        )}
        <p className="text-[10px] font-bold text-slate-400 ml-auto">
          {from || to ? 'Exibindo o intervalo selecionado' : `Exibindo ${formatMonthLabel(month)}`}
        </p>
      </div>

      {/* Totais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <SummaryCard title="Entradas" value={formatCurrency(summary.totalEntradas)} count={summary.countEntradas}
          icon={<TrendingUp className="text-emerald-500" />} tone="emerald" />
        <SummaryCard title="Saídas" value={formatCurrency(summary.totalSaidas)} count={summary.countSaidas}
          icon={<TrendingDown className="text-rose-500" />} tone="rose" />
        <SummaryCard title="Saldo" value={formatCurrency(summary.saldo)} count={summary.countEntradas + summary.countSaidas}
          icon={<Wallet className={summary.saldo >= 0 ? 'text-indigo-500' : 'text-rose-500'} />}
          tone={summary.saldo >= 0 ? 'indigo' : 'rose'} />
      </div>

      {subTab === 'geral' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm">
            <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-8">
              Movimentação diária
            </h3>
            {series.length === 0 ? (
              <EmptyState message="Nenhum lançamento no período selecionado." />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="entradas" name="Entradas" fill="#10b981" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="saidas" name="Saídas" fill="#ef4444" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm">
            <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-8">
              Saídas por categoria
            </h3>
            {expenseByCategory.length === 0 ? (
              <EmptyState message="Sem saídas no período." />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={expenseByCategory} dataKey="total" nameKey="category" innerRadius={50} outerRadius={85} paddingAngle={3}>
                      {expenseByCategory.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 mt-4">
                  {expenseByCategory.slice(0, 5).map((row, i) => (
                    <div key={row.category} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-2 font-bold text-slate-600 dark:text-slate-300 truncate">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                        {row.category}
                      </span>
                      <span className="font-black text-slate-800 dark:text-slate-100 shrink-0 ml-2">{formatCurrency(row.total)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="lg:col-span-3 bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm">
            <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-6">
              Entradas por categoria
            </h3>
            {incomeByCategory.length === 0 ? (
              <EmptyState message="Sem entradas no período." />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {incomeByCategory.map((row) => (
                  <div key={row.category} className="p-5 rounded-[1.5rem] bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/40">
                    <p className="text-[9px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 truncate">{row.category}</p>
                    <p className="text-lg font-black text-slate-900 dark:text-white mt-1">{formatCurrency(row.total)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {subTab === 'entradas' && (
        <EntryTable
          title="Entradas"
          rows={incomeRows}
          onDelete={handleDelete}
          editing={editingAmount}
          onStartEdit={startEditAmount}
          onEditChange={(value) => setEditingAmount((cur) => (cur ? { ...cur, value, error: undefined } : cur))}
          onSaveEdit={saveEditAmount}
          onCancelEdit={() => setEditingAmount(null)}
          emptyMessage="Nenhuma entrada no período. Elas são geradas ao confirmar o comparecimento na agenda ou lançadas manualmente."
          filter={
            <FilterSelect
              icon={<UserIcon size={14} />}
              label="Paciente"
              value={patientFilter}
              onChange={setPatientFilter}
              options={[
                { value: '', label: 'Visão geral (todos os pacientes)' },
                ...patients.map((p) => ({ value: p.id, label: p.name })),
              ]}
            />
          }
        />
      )}

      {subTab === 'saidas' && (
        <EntryTable
          title="Saídas"
          rows={expenseRows}
          onDelete={handleDelete}
          editing={editingAmount}
          onStartEdit={startEditAmount}
          onEditChange={(value) => setEditingAmount((cur) => (cur ? { ...cur, value, error: undefined } : cur))}
          onSaveEdit={saveEditAmount}
          onCancelEdit={() => setEditingAmount(null)}
          emptyMessage="Nenhuma saída no período."
          filter={
            <FilterSelect
              icon={<Tag size={14} />}
              label="Categoria"
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={[
                { value: '', label: 'Visão geral (todas as categorias)' },
                ...expenseCategories.map((c) => ({ value: c, label: c })),
              ]}
            />
          }
        />
      )}

      {showReport && (
        <ReportModal
          onClose={() => setShowReport(false)}
          filters={{
            ...(from || to ? { from: from || undefined, to: to || undefined } : { month }),
            patientId: patientFilter || undefined,
            professionalId: (lockedProfessionalId || professionalFilter) || undefined,
            serviceName: serviceFilter || undefined,
          }}
        />
      )}

      {modalType && (
        <EntryModal
          type={modalType}
          patients={patients}
          categories={modalType === 'entrada' ? incomeCategories : expenseCategories}
          onClose={() => setModalType(null)}
          onSave={handleCreate}
        />
      )}
    </div>
  );
};

const SummaryCard: React.FC<{
  title: string; value: string; count: number; icon: React.ReactNode; tone: 'emerald' | 'rose' | 'indigo';
}> = ({ title, value, count, icon, tone }) => {
  const toneClasses = {
    emerald: 'bg-emerald-50 dark:bg-emerald-950/20',
    rose: 'bg-rose-50 dark:bg-rose-950/20',
    indigo: 'bg-indigo-50 dark:bg-indigo-950/20',
  }[tone];

  return (
    <div className="bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm">
      <div className={`p-3 rounded-2xl w-fit mb-4 ${toneClasses}`}>{icon}</div>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{title}</p>
      <p className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter">{value}</p>
      <p className="text-[10px] font-bold text-slate-400 mt-2">{count} lançamento(s)</p>
    </div>
  );
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="py-10 sm:py-16 text-center">
    <p className="text-xs font-bold text-slate-400 max-w-md mx-auto leading-relaxed">{message}</p>
  </div>
);

const FilterSelect: React.FC<{
  icon: React.ReactNode; label: string; value: string;
  onChange: (v: string) => void; options: { value: string; label: string }[];
}> = ({ icon, label, value, onChange, options }) => (
  <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
    <span className="flex items-center gap-2 text-slate-400 text-[10px] font-black uppercase tracking-widest">
      {icon} {label}
    </span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-auto sm:min-w-[220px] cursor-pointer"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const EntryTable: React.FC<{
  title: string; rows: CashEntry[]; onDelete: (id: string) => void;
  emptyMessage: string; filter: React.ReactNode;
  /** Bloco #65: edição do valor na própria linha. */
  editing?: { id: string; value: string; error?: string; saving?: boolean } | null;
  onStartEdit?: (row: CashEntry) => void;
  onEditChange?: (value: string) => void;
  onSaveEdit?: () => void;
  onCancelEdit?: () => void;
}> = ({ title, rows, onDelete, emptyMessage, filter, editing = null, onStartEdit, onEditChange, onSaveEdit, onCancelEdit }) => {
  const total = rows.reduce((acc, r) => acc + r.amount, 0);

  return (
    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
      <div className="p-5 sm:p-8 border-b border-slate-100 dark:border-slate-800 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Filter size={16} className="text-slate-400" />
          <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">{title}</h3>
        </div>
        {filter}
        <p className="text-sm font-black text-slate-900 dark:text-white">{formatCurrency(total)}</p>
      </div>

      {rows.length === 0 ? (
        <EmptyState message={emptyMessage} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[720px]">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">
                {/* Bloco #65: Data, Paciente, Categoria, Descrição, Valor */}
                <th className="px-5 sm:px-8 py-4">Data</th>
                <th className="px-5 sm:px-8 py-4">Paciente</th>
                <th className="px-5 sm:px-8 py-4">Categoria</th>
                <th className="px-5 sm:px-8 py-4">Descrição</th>
                <th className="px-5 sm:px-8 py-4 text-right">Valor</th>
                <th className="px-5 sm:px-8 py-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                  <td className="px-5 sm:px-8 py-5 text-xs font-bold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                    {row.date.split('-').reverse().join('/')}
                  </td>
                  <td className="px-5 sm:px-8 py-5 text-xs font-bold text-slate-600 dark:text-slate-300">
                    {row.patientName || '—'}
                  </td>
                  <td className="px-5 sm:px-8 py-5">
                    <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                      {row.category}
                    </span>
                  </td>
                  <td className="px-5 sm:px-8 py-5 text-xs text-slate-500 dark:text-slate-400 font-medium max-w-[280px] truncate">
                    {row.description || '—'}
                    {row.source === 'agenda' && (
                      <span className="ml-2 text-[9px] font-black uppercase text-indigo-500">via agenda</span>
                    )}
                  </td>
                  <td className={`px-5 sm:px-8 py-5 text-right text-sm font-black whitespace-nowrap ${row.type === 'entrada' ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {editing?.id === row.id ? (
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">R$</span>
                          <input
                            autoFocus
                            inputMode="decimal"
                            value={editing.value}
                            onChange={(e) => onEditChange?.(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') onSaveEdit?.(); if (e.key === 'Escape') onCancelEdit?.(); }}
                            aria-label="Novo valor"
                            className="w-28 px-3 py-2 bg-white dark:bg-slate-800 border border-indigo-300 rounded-xl text-sm font-black text-slate-800 dark:text-white text-right outline-none focus:ring-4 focus:ring-indigo-500/10"
                          />
                          <button type="button" onClick={onSaveEdit} disabled={editing.saving} title="Salvar valor" className="p-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50">
                            <Check size={14} />
                          </button>
                          <button type="button" onClick={onCancelEdit} disabled={editing.saving} title="Cancelar" className="p-2 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-xl hover:bg-slate-200">
                            <X size={14} />
                          </button>
                        </div>
                        {editing.error && <p className="text-[10px] font-bold text-rose-600 normal-case">{editing.error}</p>}
                      </div>
                    ) : (
                      <>{row.type === 'entrada' ? '+' : '−'} {formatCurrency(row.amount)}</>
                    )}
                  </td>
                  <td className="px-5 sm:px-8 py-5 text-right whitespace-nowrap">
                    {/* Bloco #65: lápis à esquerda da lixeira, só para o valor */}
                    {onStartEdit && (
                      <button
                        onClick={() => onStartEdit(row)}
                        className="p-2.5 mr-2 bg-indigo-50 dark:bg-indigo-950/20 text-indigo-500 hover:bg-indigo-500 hover:text-white rounded-xl transition-all"
                        title="Editar valor"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(row.id)}
                      className="p-2.5 bg-rose-50 dark:bg-rose-950/20 text-rose-500 hover:bg-rose-500 hover:text-white rounded-xl transition-all"
                      title="Excluir lançamento"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const EntryModal: React.FC<{
  type: CashEntryType;
  patients: Patient[];
  categories: string[];
  onClose: () => void;
  onSave: (entry: CashEntry) => void;
}> = ({ type, patients, categories, onClose, onSave }) => {
  const isIncome = type === 'entrada';
  const [date, setDate] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState(categories[0] || '');
  const [newCategory, setNewCategory] = useState('');
  const [description, setDescription] = useState('');
  const [patientId, setPatientId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const resolvedCategory = newCategory.trim() || category;

  const handleSubmit = () => {
    const value = parseAmount(amount);
    if (!(value > 0)) return setError('Informe um valor maior que zero.');
    if (!resolvedCategory) return setError('Escolha ou crie uma categoria.');
    if (!date) return setError('Informe a data do lançamento.');

    const patient = patients.find((p) => p.id === patientId);
    onSave({
      id: `cash_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      type,
      date,
      amount: value,
      category: resolvedCategory,
      description: description.trim() || undefined,
      patientId: patient?.id,
      patientName: patient?.name,
      source: 'manual',
      createdAt: new Date().toISOString(),
    });
  };

  const inputClasses =
    'w-full p-3.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm dark:text-slate-100 focus:ring-4 focus:ring-indigo-500/10 focus:outline-none focus:border-indigo-400 transition-all';
  const labelClasses = 'text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1';

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[120] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] w-full max-w-lg border border-slate-200 dark:border-slate-800 shadow-2xl animate-in zoom-in-95 max-h-[90vh] overflow-y-auto">
        <div className="p-5 sm:p-8 border-b dark:border-slate-800 flex justify-between items-center">
          <h3 className="text-xl font-black dark:text-white tracking-tight">
            {isIncome ? 'Nova Entrada' : 'Nova Saída'}
          </h3>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-xl">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 sm:p-8 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className={labelClasses}>Data</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClasses} />
            </div>
            <div className="space-y-2">
              <label className={labelClasses}>Valor (R$)</label>
              <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" inputMode="decimal" className={inputClasses} />
            </div>
          </div>

          <div className="space-y-2">
            <label className={labelClasses}>Categoria</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClasses}>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="space-y-2">
            <label className={labelClasses}>Ou crie uma nova categoria</label>
            <input value={newCategory} onChange={(e) => setNewCategory(e.target.value)}
              placeholder={isIncome ? 'Ex.: Palestra, Supervisão' : 'Ex.: Contador, Software'} className={inputClasses} />
          </div>

          {isIncome && (
            <div className="space-y-2">
              <label className={labelClasses}>Paciente (opcional)</label>
              <select value={patientId} onChange={(e) => setPatientId(e.target.value)} className={inputClasses}>
                <option value="">Não vincular</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}

          <div className="space-y-2">
            <label className={labelClasses}>Descrição (opcional)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputClasses} />
          </div>

          {error && (
            <p className="text-xs font-bold text-rose-500 bg-rose-50 dark:bg-rose-950/30 p-4 rounded-2xl">{error}</p>
          )}
        </div>

        <div className="p-5 sm:p-8 border-t dark:border-slate-800 flex gap-4">
          <button onClick={onClose} className="flex-1 p-4 border border-slate-200 dark:border-slate-700 rounded-2xl font-black text-slate-500 uppercase text-xs tracking-widest hover:bg-slate-50 dark:hover:bg-slate-800 transition-all">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            className={`flex-1 p-4 text-white rounded-2xl font-black uppercase text-xs tracking-widest shadow-lg transition-all active:scale-95 ${isIncome ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}
          >
            Lançar
          </button>
        </div>
      </div>
    </div>
  );
};


/** Bloco #66: conteúdo × formato × entrega, respeitando os filtros ativos. */
const ReportModal: React.FC<{
  onClose: () => void;
  filters: Pick<CashReportRequest, 'from' | 'to' | 'month' | 'patientId' | 'professionalId' | 'serviceName'>;
}> = ({ onClose, filters }) => {
  const [content, setContent] = useState<CashReportRequest['content']>('consolidado');
  const [format, setFormat] = useState<CashReportRequest['format']>('PDF');
  const [delivery, setDelivery] = useState<CashReportRequest['delivery']>('download');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  const run = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const r = await requestCashReport({ content, format, delivery, ...filters });
      if ('base64' in r) {
        const bytes = Uint8Array.from(atob(r.base64), (c) => c.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], { type: r.contentType }));
        const a = document.createElement('a');
        a.href = url;
        a.download = r.filename;
        a.click();
        URL.revokeObjectURL(url);
        setFeedback({ ok: true, msg: `Arquivo ${r.filename} gerado.` });
      } else {
        setFeedback({ ok: true, msg: `Relatório enviado para ${r.to} (${r.filename}).` });
      }
    } catch (err) {
      setFeedback({ ok: false, msg: describeError(err) });
    } finally {
      setBusy(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Option = ({ value, current, set, label }: { value: string; current: string; set: (v: any) => void; label: string }) => (
    <button
      type="button"
      onClick={() => set(value)}
      className={`flex-1 py-3 px-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all ${
        current === value ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-slate-900 text-slate-500 border-slate-200 dark:border-slate-700 hover:border-indigo-400'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-2xl p-5 sm:p-8 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Exportar relatório</h3>
            <p className="text-[11px] text-slate-500 font-medium mt-1">Respeita os filtros ativos (período, paciente, profissional e tipo de serviço).</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Fechar"><X size={18} /></button>
        </div>
        <div className="space-y-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Conteúdo</p>
          <div className="flex gap-2">
            <Option value="consolidado" current={content} set={setContent} label="Consolidado" />
            <Option value="entradas" current={content} set={setContent} label="Só entradas" />
            <Option value="saidas" current={content} set={setContent} label="Só saídas" />
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Formato</p>
          <div className="flex gap-2">
            <Option value="PDF" current={format} set={setFormat} label="PDF" />
            <Option value="CSV" current={format} set={setFormat} label="CSV" />
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Entrega</p>
          <div className="flex gap-2">
            <Option value="download" current={delivery} set={setDelivery} label="Baixar o arquivo" />
            <Option value="email" current={delivery} set={setDelivery} label="Enviar por e-mail" />
          </div>
          {delivery === 'email' && <p className="text-[10px] text-slate-400 font-medium">O arquivo vai para o e-mail cadastrado do usuário logado.</p>}
        </div>
        {feedback && (
          <div role="status" className={`px-5 py-4 rounded-2xl text-xs font-bold ${feedback.ok ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300' : 'bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300'}`}>
            {feedback.msg}
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">Fechar</button>
          <button type="button" onClick={() => void run()} disabled={busy} className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : delivery === 'email' ? <Send size={14} /> : <Download size={14} />}
            {delivery === 'email' ? 'Enviar' : 'Gerar e baixar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Finance;
